import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  AccountTree,
  Business,
} from '@mui/icons-material';

interface NetworkNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  type: 'fund' | 'administrator' | 'custodian' | 'auditor' | 'investment_manager';
  size: number;
  color: string;
}

interface NetworkLink extends d3.SimulationLinkDatum<NetworkNode> {
  source: string | NetworkNode;
  target: string | NetworkNode;
  type: string;
  strength: number;
}

interface FundNetworkVisualizationProps {
  fundStructures: any[];
  selectedFund?: any;
  onNodeClick?: (node: NetworkNode) => void;
  onLinkClick?: (link: NetworkLink) => void;
}

const NODE_COLORS: Record<string, string> = {
  fund: '#2196f3',
  administrator: '#4caf50',
  custodian: '#ff9800',
  auditor: '#9c27b0',
  investment_manager: '#f44336',
};

const NODE_ICONS: Record<string, string> = {
  fund: '\uf1ec',
  administrator: '\uf19c',
  custodian: '\uf132',
  auditor: '\uf002',
  investment_manager: '\uf201',
};

const FundNetworkVisualization: React.FC<FundNetworkVisualizationProps> = ({
  fundStructures,
  selectedFund,
  onNodeClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<NetworkNode, NetworkLink> | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'selected' | 'funds'>('all');
  const theme = useTheme();

  const buildNetworkData = useCallback(() => {
    if (!fundStructures || fundStructures.length === 0) {
      return { nodes: [] as NetworkNode[], links: [] as NetworkLink[] };
    }

    const nodes: NetworkNode[] = [];
    const links: NetworkLink[] = [];
    const nodeMap = new Map<string, boolean>();

    fundStructures.forEach((structure) => {
      const fund = structure.fund;
      if (!nodeMap.has(fund.id)) {
        const aum = fund.total_assets || fund.aum || 1000000;
        nodes.push({
          id: fund.id,
          label: fund.name,
          type: 'fund',
          size: Math.max(28, Math.min(55, Math.log10(aum) * 7)),
          color: NODE_COLORS.fund,
        });
        nodeMap.set(fund.id, true);
      }

      (structure.serviceProviders || []).forEach((provider: any) => {
        if (!nodeMap.has(provider.id)) {
          const pType = (provider.provider_type || 'administrator').toLowerCase();
          nodes.push({
            id: provider.id,
            label: provider.name,
            type: pType as any,
            size: 18,
            color: NODE_COLORS[pType] || '#666',
          });
          nodeMap.set(provider.id, true);
        }

        links.push({
          source: fund.id,
          target: provider.id,
          type: provider.provider_type || 'service',
          strength: 0.7,
        });
      });
    });

    return { nodes, links };
  }, [fundStructures]);

  const getFilteredData = useCallback(() => {
    const data = buildNetworkData();

    if (viewMode === 'selected' && selectedFund) {
      const structure = fundStructures.find((s: any) => s.fund.id === selectedFund.id);
      if (!structure) return { nodes: [], links: [] };

      const providerIds = new Set((structure.serviceProviders || []).map((p: any) => p.id));
      return {
        nodes: data.nodes.filter(n => n.id === selectedFund.id || providerIds.has(n.id)),
        links: data.links.filter(l => {
          const src = typeof l.source === 'string' ? l.source : (l.source as NetworkNode).id;
          const tgt = typeof l.target === 'string' ? l.target : (l.target as NetworkNode).id;
          return src === selectedFund.id || tgt === selectedFund.id;
        }),
      };
    } else if (viewMode === 'funds') {
      return { nodes: data.nodes.filter(n => n.type === 'fund'), links: [] };
    }
    return data;
  }, [buildNetworkData, viewMode, selectedFund, fundStructures]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth || 800;
    const height = 500;

    svg.attr('width', width).attr('height', height);
    svg.selectAll('*').remove();

    const data = getFilteredData();
    if (data.nodes.length === 0) return;

    // Deep copy nodes/links for D3 mutation
    const nodes: NetworkNode[] = data.nodes.map(d => ({ ...d }));
    const links: NetworkLink[] = data.links.map(d => ({ ...d }));

    // Defs for gradients and filters
    const defs = svg.append('defs');

    // Glow filter
    const filter = defs.append('filter').attr('id', 'glow');
    filter.append('feGaussianBlur').attr('stdDeviation', '3').attr('result', 'coloredBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Drop shadow
    const shadow = defs.append('filter').attr('id', 'shadow').attr('x', '-20%').attr('y', '-20%').attr('width', '140%').attr('height', '140%');
    shadow.append('feDropShadow').attr('dx', '0').attr('dy', '2').attr('stdDeviation', '3').attr('flood-color', 'rgba(0,0,0,0.5)');

    // Arrow markers per type
    Object.entries(NODE_COLORS).forEach(([type, color]) => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 20)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', color)
        .attr('opacity', 0.6);
    });

    // Zoom behaviour
    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoomBehavior);

    const g = svg.append('g');

    // Simulation
    const simulation = d3.forceSimulation<NetworkNode>(nodes)
      .force('link', d3.forceLink<NetworkNode, NetworkLink>(links)
        .id(d => d.id)
        .distance(d => {
          const src = d.source as NetworkNode;
          return src.type === 'fund' ? 150 : 120;
        })
        .strength(0.5))
      .force('charge', d3.forceManyBody().strength(-400).distanceMax(500))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide<NetworkNode>().radius(d => d.size + 10))
      .force('x', d3.forceX(width / 2).strength(0.05))
      .force('y', d3.forceY(height / 2).strength(0.05));

    simulationRef.current = simulation;

    // Links (edges)
    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => {
        const tgt = d.target as NetworkNode;
        return NODE_COLORS[(tgt.type || (typeof d.type === 'string' ? d.type : 'administrator')).toLowerCase()] || '#555';
      })
      .attr('stroke-opacity', 0.35)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,3')
      .attr('marker-end', d => {
        const tgt = d.target as NetworkNode;
        const type = (tgt.type || '').toLowerCase();
        return `url(#arrow-${type})`;
      });

    // Link hover labels
    const linkLabel = g.append('g')
      .selectAll('text')
      .data(links)
      .join('text')
      .text(d => (typeof d.type === 'string' ? d.type : '').replace('_', ' '))
      .attr('font-size', '9px')
      .attr('fill', theme.palette.text.secondary)
      .attr('text-anchor', 'middle')
      .attr('opacity', 0);

    // Node groups
    const node = g.append('g')
      .selectAll<SVGGElement, NetworkNode>('g')
      .data(nodes)
      .join('g')
      .attr('cursor', 'pointer')
      .call(d3.drag<SVGGElement, NetworkNode>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Outer glow ring for funds
    node.filter(d => d.type === 'fund')
      .append('circle')
      .attr('r', d => d.size + 6)
      .attr('fill', 'none')
      .attr('stroke', d => d.color)
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.2)
      .attr('filter', 'url(#glow)');

    // Main circle
    node.append('circle')
      .attr('r', d => d.size)
      .attr('fill', d => d.color)
      .attr('fill-opacity', 0.85)
      .attr('stroke', d => d3.color(d.color)?.brighter(0.8)?.toString() || '#fff')
      .attr('stroke-width', d => d.type === 'fund' ? 2.5 : 1.5)
      .attr('filter', 'url(#shadow)');

    // Inner gradient overlay
    node.append('circle')
      .attr('r', d => d.size * 0.7)
      .attr('fill', 'white')
      .attr('fill-opacity', 0.08);

    // Type icon (first letter as fallback)
    node.append('text')
      .text(d => {
        switch (d.type) {
          case 'fund': return 'F';
          case 'administrator': return 'A';
          case 'custodian': return 'C';
          case 'auditor': return 'Au';
          case 'investment_manager': return 'IM';
          default: return '?';
        }
      })
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', d => d.type === 'fund' ? '14px' : '10px')
      .attr('font-weight', '700')
      .attr('fill', 'white')
      .attr('opacity', 0.9)
      .attr('font-family', '"JetBrains Mono", monospace');

    // Label below node
    node.append('text')
      .text(d => d.label.length > 22 ? d.label.substring(0, 20) + '...' : d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', d => d.size + 16)
      .attr('font-size', d => d.type === 'fund' ? '12px' : '10px')
      .attr('font-weight', d => d.type === 'fund' ? '600' : '400')
      .attr('fill', theme.palette.text.primary)
      .attr('font-family', '"Inter", sans-serif');

    // AUM label for funds
    node.filter(d => d.type === 'fund')
      .append('text')
      .text(d => {
        const structure = fundStructures.find((s: any) => s.fund.id === d.id);
        const aum = structure?.fund?.total_assets || structure?.fund?.aum;
        if (!aum) return '';
        if (aum >= 1e9) return `EUR ${(aum / 1e9).toFixed(1)}B`;
        if (aum >= 1e6) return `EUR ${(aum / 1e6).toFixed(0)}M`;
        return `EUR ${aum.toLocaleString()}`;
      })
      .attr('text-anchor', 'middle')
      .attr('dy', d => d.size + 30)
      .attr('font-size', '9px')
      .attr('fill', theme.palette.text.secondary)
      .attr('font-family', '"JetBrains Mono", monospace');

    // Tooltip
    const tooltip = d3.select(containerRef.current)
      .append('div')
      .attr('class', 'network-tooltip')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', theme.palette.background.paper)
      .style('border', `1px solid ${theme.palette.divider}`)
      .style('border-radius', '8px')
      .style('padding', '10px 14px')
      .style('font-size', '12px')
      .style('font-family', '"Inter", sans-serif')
      .style('color', theme.palette.text.primary)
      .style('box-shadow', '0 4px 20px rgba(0,0,0,0.4)')
      .style('opacity', 0)
      .style('z-index', 1000)
      .style('max-width', '260px');

    // Interactions
    node.on('mouseover', function (event, d) {
      d3.select(this).select('circle:nth-child(2)')
        .transition().duration(200)
        .attr('r', d.size * 1.15)
        .attr('fill-opacity', 1);

      // Highlight connected links
      link.transition().duration(200)
        .attr('stroke-opacity', l => {
          const src = (l.source as NetworkNode).id;
          const tgt = (l.target as NetworkNode).id;
          return src === d.id || tgt === d.id ? 0.8 : 0.1;
        })
        .attr('stroke-width', l => {
          const src = (l.source as NetworkNode).id;
          const tgt = (l.target as NetworkNode).id;
          return src === d.id || tgt === d.id ? 2.5 : 1;
        });

      // Show link labels for connected links
      linkLabel.transition().duration(200)
        .attr('opacity', l => {
          const src = (l.source as NetworkNode).id;
          const tgt = (l.target as NetworkNode).id;
          return src === d.id || tgt === d.id ? 1 : 0;
        });

      // Dim unrelated nodes
      node.transition().duration(200)
        .attr('opacity', n => {
          if (n.id === d.id) return 1;
          const connected = links.some(l => {
            const src = (l.source as NetworkNode).id;
            const tgt = (l.target as NetworkNode).id;
            return (src === d.id && tgt === n.id) || (tgt === d.id && src === n.id);
          });
          return connected ? 1 : 0.25;
        });

      const structure = fundStructures.find((s: any) => s.fund.id === d.id);
      const aum = structure?.fund?.total_assets || structure?.fund?.aum;
      const connections = links.filter(l => {
        const src = (l.source as NetworkNode).id;
        const tgt = (l.target as NetworkNode).id;
        return src === d.id || tgt === d.id;
      }).length;

      let html = `<div style="font-weight:700;margin-bottom:4px;color:${d.color}">${d.label}</div>`;
      html += `<div style="color:${theme.palette.text.secondary};text-transform:capitalize;margin-bottom:4px">${d.type.replace('_', ' ')}</div>`;
      if (aum) {
        const formatted = aum >= 1e9 ? `EUR ${(aum / 1e9).toFixed(2)}B` : `EUR ${(aum / 1e6).toFixed(1)}M`;
        html += `<div>AUM: <strong>${formatted}</strong></div>`;
      }
      html += `<div>Connections: <strong>${connections}</strong></div>`;

      tooltip
        .html(html)
        .style('left', `${event.offsetX + 15}px`)
        .style('top', `${event.offsetY - 10}px`)
        .transition().duration(200)
        .style('opacity', 1);
    })
    .on('mouseout', function (event, d) {
      d3.select(this).select('circle:nth-child(2)')
        .transition().duration(300)
        .attr('r', d.size)
        .attr('fill-opacity', 0.85);

      link.transition().duration(300)
        .attr('stroke-opacity', 0.35)
        .attr('stroke-width', 1.5);

      linkLabel.transition().duration(300).attr('opacity', 0);

      node.transition().duration(300).attr('opacity', 1);

      tooltip.transition().duration(200).style('opacity', 0);
    })
    .on('click', (event, d) => {
      onNodeClick?.(d);
    });

    // Simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => (d.source as NetworkNode).x || 0)
        .attr('y1', d => (d.source as NetworkNode).y || 0)
        .attr('x2', d => (d.target as NetworkNode).x || 0)
        .attr('y2', d => (d.target as NetworkNode).y || 0);

      linkLabel
        .attr('x', d => (((d.source as NetworkNode).x || 0) + ((d.target as NetworkNode).x || 0)) / 2)
        .attr('y', d => (((d.source as NetworkNode).y || 0) + ((d.target as NetworkNode).y || 0)) / 2);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Initial zoom to fit
    simulation.on('end', () => {
      const bounds = (g.node() as SVGGElement)?.getBBox();
      if (bounds) {
        const pad = 40;
        const scale = Math.min(
          width / (bounds.width + pad * 2),
          height / (bounds.height + pad * 2),
          1.5
        );
        const tx = width / 2 - (bounds.x + bounds.width / 2) * scale;
        const ty = height / 2 - (bounds.y + bounds.height / 2) * scale;
        svg.transition().duration(750)
          .call(zoomBehavior.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
      }
    });

    return () => {
      simulation.stop();
      tooltip.remove();
    };
  }, [getFilteredData, theme, fundStructures, onNodeClick]);

  const handleZoomIn = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(
      d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.3, 4]).on('zoom', () => {}).scaleBy as any,
      1.3
    );
  };

  const handleZoomOut = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(
      d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.3, 4]).on('zoom', () => {}).scaleBy as any,
      0.7
    );
  };

  const handleResetView = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().duration(500).call(
      d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.3, 4]).on('zoom', () => {}).transform as any,
      d3.zoomIdentity
    );
  };

  const data = getFilteredData();

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={1}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>View Mode</InputLabel>
            <Select
              value={viewMode}
              label="View Mode"
              onChange={(e) => setViewMode(e.target.value as any)}
            >
              <MenuItem value="all">All Networks</MenuItem>
              <MenuItem value="selected">Selected Fund</MenuItem>
              <MenuItem value="funds">Funds Only</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset View">
            <IconButton onClick={handleResetView} size="small">
              <CenterFocusStrong />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {Object.entries(NODE_COLORS).map(([type, color]) => (
          <Chip
            key={type}
            icon={type === 'fund' ? <AccountTree /> : <Business />}
            label={type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            size="small"
            sx={{
              bgcolor: `${color}22`,
              color: color,
              borderColor: `${color}44`,
              fontWeight: 600,
              '& .MuiChip-icon': { color: color },
            }}
            variant="outlined"
          />
        ))}
      </Box>

      {/* Network SVG */}
      <Paper
        ref={containerRef}
        sx={{
          height: 500,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <svg
          ref={svgRef}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />

        {data.nodes.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <AccountTree sx={{ fontSize: 48, color: 'grey.600', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Network Data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewMode === 'selected'
                ? 'Select a fund to view its network'
                : 'No fund structures available to visualize'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Statistics */}
      <Box mt={2} display="flex" gap={2} flexWrap="wrap">
        <Chip
          label={`${data.nodes.length} Nodes`}
          variant="outlined"
          size="small"
          sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}
        />
        <Chip
          label={`${data.links.length} Connections`}
          variant="outlined"
          size="small"
          sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}
        />
        <Chip
          label={`${data.nodes.filter(n => n.type === 'fund').length} Funds`}
          variant="outlined"
          size="small"
          color="primary"
          sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}
        />
        <Chip
          label={`${data.nodes.filter(n => n.type !== 'fund').length} Providers`}
          variant="outlined"
          size="small"
          color="success"
          sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}
        />
      </Box>
    </Box>
  );
};

export default FundNetworkVisualization;
